// Sanitise a CMS rich-text string before it is injected via `v-html`.
//
// CMS wysiwyg fields (e.g. iconomy_footer.registerInfo) legitimately carry
// formatting — <p>, <a>, <strong> — so they can't be rendered as plain text.
// But the same field would run any <script>, event handler, or javascript:
// href an attacker with CMS write access (or a compromised CMS host) put in
// it, on every page the field renders. So keep the formatting but strip the
// executable surface via an allowlist, using the browser's parser rather than
// regex so malformed/obfuscated markup can't slip past. Mirrors sanitizeSvg.

const ALLOWED_TAGS = new Set([
  "p", "br", "span", "div", "b", "strong", "i", "em", "u", "s", "small",
  "sub", "sup", "mark", "code", "blockquote", "a", "ul", "ol", "li",
  "h1", "h2", "h3", "h4", "h5", "h6",
  // legal/privacy copy uses headings + the occasional data table
  "table", "thead", "tbody", "tr", "th", "td",
]);

// executable/opaque nodes are dropped whole (with their subtree); any other
// non-allowlisted tag is unwrapped so its text survives
const DANGEROUS_TAGS = new Set([
  "script", "style", "iframe", "object", "embed", "template", "link", "meta",
  "svg", "math", "form", "input", "button",
]);

// per-tag attribute allowlist; anything not listed is dropped
const ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(["href", "target", "rel", "title"]),
  "*": new Set(["class", "title"]),
};

const URL_ATTRS = ["href", "src"];
// schemes that may appear in a URL attribute. Schemeless values (relative
// paths, "#frag", "//host") carry no scheme and are allowed; a value that
// does carry a scheme must be one of these — blocks javascript:/data:/vbscript:
const SAFE_SCHEMES = new Set(["http", "https", "mailto", "tel"]);

function attrAllowed(tag: string, attr: string): boolean {
  if (attr.startsWith("on")) return false;
  const perTag = ALLOWED_ATTRS[tag];
  if (perTag && perTag.has(attr)) return true;
  return ALLOWED_ATTRS["*"].has(attr);
}

function urlSafe(value: string): boolean {
  // strip control chars so "java\tscript:" style obfuscation can't hide a scheme
  const cleaned = value.replace(/[\x00-\x20]/g, "").toLowerCase();
  const scheme = cleaned.match(/^([a-z][a-z0-9+.-]*):/);
  if (!scheme) return true; // relative / fragment / protocol-relative
  return SAFE_SCHEMES.has(scheme[1]);
}

function cleanAttrs(el: Element): void {
  const tag = el.tagName.toLowerCase();
  Array.from(el.attributes).forEach((attr) => {
    const name = attr.name.toLowerCase();
    if (!attrAllowed(tag, name)) {
      el.removeAttribute(attr.name);
      return;
    }
    if (URL_ATTRS.includes(name) && !urlSafe(attr.value)) {
      el.removeAttribute(attr.name);
    }
  });
  // any link opening a new tab loses access to this window (tabnabbing)
  if (tag === "a" && el.getAttribute("target") === "_blank") {
    el.setAttribute("rel", "noopener noreferrer");
  }
}

// scrub the subtree first, then decide keep/unwrap/drop for each child, so an
// unwrapped wrapper can never re-expose a dangerous node from inside it
function scrub(el: Element): void {
  Array.from(el.children).forEach((child) => {
    const tag = child.tagName.toLowerCase();
    if (DANGEROUS_TAGS.has(tag)) {
      child.remove();
      return;
    }
    scrub(child);
    if (ALLOWED_TAGS.has(tag)) {
      cleanAttrs(child);
    } else {
      // unknown-but-harmless wrapper: keep its (already-clean) contents
      child.replaceWith(...Array.from(child.childNodes));
    }
  });
}

export function sanitizeHtml(html: string): string {
  if (!html || typeof html !== "string") return "";
  // no DOM (SSR/prerender) — drop rather than emit unsanitised markup
  if (typeof window === "undefined" || typeof DOMParser === "undefined") return "";

  try {
    const doc = new DOMParser().parseFromString(html, "text/html");
    scrub(doc.body);
    return doc.body.innerHTML;
  } catch {
    return "";
  }
}

export default sanitizeHtml;
